import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Loader, Popup, Segment } from 'semantic-ui-react';
import { CustomSegment } from '../../../../components';
import { ActivityData } from './activityData';
import { ActivityInput } from './activityInput';
import { ActivityOutput } from '../activityPlan/activityOutput';
import { GroupParticipants } from './groupParticipants';
import { ActivityMemberSteps } from './activityMemberSteps';
import { GrantProgramData } from './grantProgramData';
import { getPageDisable } from '../../../../utils';
import {
  getEvaluationGroups,
  getEvaluationPlans,
} from '../../../../store/actions';
import { get, map, flattenDeep, isEmpty } from '../../../../utils/lodash';
import {
  isFestivalProgramActivity,
  isGrantProgramActivity,
  isMapProgramActivity,
  isParentProgramActivity,
} from '../../../../utils/activity';
import { formatEvaluationPlanDisplayName } from '../../../../utils/evaluationPlan';
import LinkedActivities from './linkedActivity';

const groupLiveCount = (group) =>
  Number(get(group, 'actualSize', null) ?? get(group, 'size', 0));
const getUnusedGroupMessages = (
  currentActivity,
  evaluationGroupSelected,
  t
) => {
  const allGroups = evaluationGroupSelected || [];
  if (isEmpty(allGroups)) return [];

  const evaluationPlans = get(currentActivity, 'evaluationPlans', []) || [];
  if (isEmpty(evaluationPlans)) return [];

  const referencedGroupIds = new Set(
    flattenDeep(
      map(evaluationPlans, (plan) =>
        map(get(plan, 'participantGroups', []), (pg) =>
          get(pg, 'targetGroup.id', null)
        )
      )
    ).filter(Boolean)
  );

  // Participant values issues... has values but not in a plan
  return allGroups
    .filter(
      (group) => groupLiveCount(group) > 0 && !referencedGroupIds.has(group.id)
    )
    .map((group) => {
      const groupName = get(group, 'name', '-');
      const count = groupLiveCount(group);
      return {
        text: t('activity.activity_plan.group_not_in_any_plan', {
          groupName,
          count,
        }),
        tooltip: t('activity.activity_plan.group_not_in_any_plan_tooltip', {
          groupName,
          count,
        }),
      };
    });
};

// Sub Group > Total Participants error:)
const getOverLimitSubGroupMessages = (
  currentActivity,
  evaluationGroupSelected,
  t
) => {
  const totalParticipants = Number(
    get(currentActivity, 'actualParticipants', 0)
  );
  if (totalParticipants <= 0) return [];

  return (evaluationGroupSelected || [])
    .filter((group) => get(group, 'type') === 'GROUP')
    .filter((group) => groupLiveCount(group) > totalParticipants)
    .map((group) => ({
      text: t('activity.activity_plan.subgroup_exceeds_total_participants', {
        groupName: get(group, 'name', '-'),
        count: groupLiveCount(group),
        total: totalParticipants,
      }),
      tooltip: t(
        'activity.activity_plan.subgroup_exceeds_total_participants_tooltip'
      ),
    }));
};

const planGroupLiveCount = (group) =>
  get(group, 'participantCount', null) ??
  get(group, 'targetGroup.actualSize', null) ??
  get(group, 'targetGroup.size', null);

const isPlanSelfAssessed = (plan) =>
  get(plan, 'method.response', '') === 'Self-assessed' ||
  get(plan, 'methodId', null) === 9;

const getPlanResponseCount = (plan) => {
  const planLevel = Number(get(plan, 'actualParticipantsEvaluated', 0)) || 0;
  const fromQuestions = Math.max(
    0,
    ...map(get(plan, 'evaluationQuestions', []), (question) =>
      Number(get(question, 'actualParticipantsEvaluated', 0))
    )
  );
  const hasResponses =
    planLevel > 0 ||
    fromQuestions > 0 ||
    Boolean(get(plan, 'haveResponses', false));
  return Math.max(planLevel, fromQuestions, hasResponses ? 1 : 0);
};

// resonses > participants in plan (error persists on activity plan page even when eval plans are complete)
const getResponsesExceedGroupMessages = (currentActivity, t) => {
  const plans = get(currentActivity, 'evaluationPlans', []) || [];

  return plans
    .map((plan) => {
      const groups = get(plan, 'participantGroups', []) || [];
      const counts = groups
        .map(planGroupLiveCount)
        .filter((count) => count != null)
        .map(Number);
      if (isEmpty(counts)) return null;
      const total = counts.reduce((sum, count) => sum + count, 0);

      const respondentsCount = getPlanResponseCount(plan);
      if (respondentsCount <= total) return null;

      const breakdown = groups
        .map((group) => {
          const count = planGroupLiveCount(group);
          const name = get(group, 'targetGroup.name', '-');
          return count != null ? `${name} - ${count}` : null;
        })
        .filter(Boolean)
        .join(', ');

      return {
        text: t(
          isPlanSelfAssessed(plan)
            ? 'activity.activity_plan.plan_participants_considered_exceed_groups'
            : 'activity.activity_plan.plan_responses_exceed_groups',
          {
            planName: formatEvaluationPlanDisplayName(plan),
            count: respondentsCount,
            breakdown,
            total,
          }
        ),
        tooltip: null,
      };
    })
    .filter(Boolean);
};

// if errors exsist, new potential errors section appears (not when activity is archieved)
const PotentialIssuesBox = ({
  currentActivity,
  evaluationGroupSelected,
  t,
}) => {
  if (get(currentActivity, 'statusId', null) === 3) return null;

  const messages = [
    ...getUnusedGroupMessages(currentActivity, evaluationGroupSelected, t),
    ...getOverLimitSubGroupMessages(
      currentActivity,
      evaluationGroupSelected,
      t
    ),
    ...getResponsesExceedGroupMessages(currentActivity, t),
  ];
  if (isEmpty(messages)) return null;

  return (
    <div className="issues-box-info activity-plan-potential-issues">
      <p className="fw-bold">{t('activity.activity_plan.potential_issues')}</p>
      <div className="potential-issues-panel">
        {messages.map(({ text, tooltip }, i) => (
          <div className="padding-issue" key={i}>
            {tooltip ? (
              <Popup
                trigger={
                  <Icon name="info circle" className="issue-info-icon" />
                }
                content={tooltip}
                className="issue-info-popup"
                position="right center"
              />
            ) : (
              <Icon name="info circle" />
            )}
            <span>{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ActivityPlan = ({ user, currentActivity, actPut, ...props }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const evaluationGroupSelected = useSelector(
    (state) => state.activity.evaluationGroupSelected
  );
  const pageDisabled = getPageDisable(
    currentActivity,
    user,
    'project.edit.activityPlan'
  );
  const coreGroupPageDisabled = getPageDisable(
    currentActivity,
    user,
    'project.edit.activityPlan',
    true
  );

  // Lets link land directly on a specific section of this tab:)
  useEffect(() => {
    const scrollTo = location.state?.scrollTo;
    if (!scrollTo) return;

    let settleTimer = null;
    let stopped = false;

    const attemptScroll = () => {
      const el = document.querySelector(`.${scrollTo}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    const scheduleAttempt = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(attemptScroll, 250);
    };

    scheduleAttempt();

    const observer = new MutationObserver(() => {
      if (!stopped) scheduleAttempt();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const giveUp = setTimeout(() => {
      stopped = true;
      observer.disconnect();
    }, 5000);

    return () => {
      stopped = true;
      observer.disconnect();
      clearTimeout(settleTimer);
      clearTimeout(giveUp);
    };
  }, [location.state]);

  useEffect(() => {
    if (!currentActivity?.id) return;
    dispatch(getEvaluationGroups(currentActivity.id));
    dispatch(getEvaluationPlans(currentActivity.id));
  }, [currentActivity?.id]);

  return (
    <div>
      <CustomSegment
        title={
          isParentProgramActivity(currentActivity.activityPlanTypeId)
            ? t('activity.activity_plan.program_description')
            : currentActivity.statusId >= 2
              ? t('activity.activity_plan.manage_activity')
              : t('activity.activity_plan.setup_activity')
        }
        className="program-activity-description"
        children={<ActivityData currentState="project.edit.activityPlan" />}
        rightHeader={
          <Loader
            active={actPut.activityData ? actPut.activityData : false}
            inline
          />
        }
      />

      {user && currentActivity && (
        <CustomSegment
          className="activity-members"
          title={t('activity.activity_plan.activity_members')}
          subTitle={t('activity.activity_plan.invite_organisation_members')}
          children={<ActivityMemberSteps {...props} />}
          rightHeader={
            <Loader
              active={actPut.activityMembers ? actPut.activityMembers : false}
              inline
            />
          }
          extraMargin
        />
      )}

      <>
        <CustomSegment
          className="projected-inputs"
          title={
            currentActivity.statusId === 1
              ? t('activity.activity_plan.projected_inputs')
              : t('activity.activity_plan.actual_inputs')
          }
          // 'Provide details for projected expected cost and requirements'
          children={
            <ActivityInput
              actual={currentActivity.statusId !== 1}
              currentState="project.edit.activityPlan"
            />
          }
          rightHeader={
            <Loader
              active={actPut.projectInput ? actPut.projectInput : false}
              inline
            />
          }
          extraMargin
        />
        <CustomSegment
          className="projected-outputs"
          title={
            currentActivity.statusId === 1
              ? t('activity.activity_plan.projected_outputs')
              : t('activity.activity_plan.actual_outputs')
          }
          children={
            actPut.activityData ? (
              <Segment loading className="loading-segment" />
            ) : (
              <>
                <ActivityOutput
                  actual={currentActivity.statusId !== 1}
                  currentState="project.edit.activityPlan"
                />
                <PotentialIssuesBox
                  currentActivity={currentActivity}
                  evaluationGroupSelected={evaluationGroupSelected}
                  t={t}
                />
              </>
            )
          }
          rightHeader={
            <Loader
              active={actPut.projectedOutputs ? actPut.projectedOutputs : false}
              inline
            />
          }
          extraMargin
          disabled={pageDisabled}
        />
      </>

      {currentActivity.activityPlanTypeId === 1 && (
        <CustomSegment
          className="core-participant-group"
          title={t('activity.activity_plan.evaluation_subgroups')}
          children={
            actPut.activityData ? (
              <Segment loading className="loading-segment" />
            ) : (
              <GroupParticipants
                {...props}
                currentState="project.edit.activityPlan"
                pageDisabled={coreGroupPageDisabled}
              />
            )
          }
          extraMargin
        />
      )}

      {/* Grant Program */}
      {currentActivity.activityPlanTypeId === 4 &&
        currentActivity.statusId > 1 && (
          <CustomSegment
            noPrint
            title={t('activity.activity_plan.grants_program')}
            children={<GrantProgramData {...props} />}
            extraMargin
          />
        )}

      {/* Need to show linked activities table Festival and MAP for all the status
       * but grant linked activities need to show after green light
       */}
      {isFestivalProgramActivity(currentActivity.activityPlanTypeId) || // Festival & MAP
      isMapProgramActivity(currentActivity.activityPlanTypeId) ||
      (isGrantProgramActivity(currentActivity.activityPlanTypeId) &&
        currentActivity.statusId > 1) ? (
        <div className="mt-4 program-activities-section">
          <LinkedActivities extraBottomMargin />
        </div>
      ) : null}
    </div>
  );
};

export default ActivityPlan;
